import type { Schemas } from '@argzombie/api-client';
import { CORE_STAT_IDS, PERK_FLAGS, type BackgroundDef } from '../skills/types';

/*
 * The homebase redesign content (cards 3-8; ADR 0046, 0049, 0048; the stats moved to lib/skills, ADR 0066): shared-cs
 * HomeRulesDef.cs, InventoryDefs.cs, HomeSkillDefs.cs and the new fields on ItemDef, BaseModuleLevelDef, ConditionDef and BaseRulesDef.
 * Leaf types come from the generated client; the blocks keep local nullable supersets (explicit `null` nested blocks and entries) so the
 * validator can report them like the server. Every field is optional: an absent one is its C# default (`lib/hb/defaults.ts`).
 */

/** shared-cs `StatIds.All` (ADR 0066), in sheet order. The ids and the 0..10 scale are code; content tunes the numbers. */
export const STAT_IDS = CORE_STAT_IDS;
export type StatId = (typeof STAT_IDS)[number];
/** `StatIds.Min`, `.Max`, `.Neutral` (5: "an ordinary adult", the pivot a baseline sheet plays like today at). */
export const STAT_MIN = 0;
export const STAT_MAX = 10;
export const STAT_NEUTRAL = 5;
/** `StatIds.Label` / `Short`: STR, END, REF, WIT, NRV, CHA (an unknown id in capitals). */
export const statLabel = (id: string | null | undefined) => (id ?? '').toUpperCase();

/** shared-cs `PerkFlags.All` (re-exported from lib/skills/types). */
export { PERK_FLAGS };

/** shared-cs `BackgroundDef` (`backgrounds[]`): internal only, never shown to players (not a role). Bias keys `stat:<id>` / `skill:<id>`. */
export type { BackgroundDef };

/** shared-cs `HomeRulesDef` (`homeRules`). */
export type HomeBedRulesDef = Schemas['HomeBedRulesDef'];
export type HeadlineRulesDef = Schemas['HeadlineRulesDef'];
export type SupplyRulesDef = Schemas['SupplyRulesDef'];
export type HomeRulesDef = {
  beds?: HomeBedRulesDef | null;
  buildMarkMinSeconds?: number;
  dischargeMarkMinHours?: number;
  dischargeMarkMinHeal?: number;
  /** WIND (contract 3.7): rest debt (winds) a bed stay must clear for a CARE mark; replaces dischargeMarkMinHeal. */
  dischargeMarkMinRest?: number;
  homeMarksPerDay?: number;
  headline?: HeadlineRulesDef | null;
  supplies?: SupplyRulesDef | null;
  onTableMax?: number;
  logMax?: number;
};

/** shared-cs `ItemGroupDef` (`storageGroups[]`, `buildCategories[]`) and its `ItemMatchDef`. */
export type ItemMatchDef = Schemas['ItemMatchDef'];
export type ItemGroupDef = {
  id?: string;
  name?: string;
  order?: number;
  match?: ItemMatchDef | null;
};
/** `StorageGroupIds.Other`: the catch-all shelf, never content. */
export const STORAGE_GROUP_OTHER = 'other';
/** `BuildCategoryIds.IsReserved`: Build's own tabs. */
export const BUILD_CATEGORY_RESERVED = ['next_level', 'base', 'other'] as const;

/** shared-cs `InventoryRulesDef` (`inventoryRules`; null/absent = every limit enforced, the legacy behaviour). */
/** `raidKitHeldOnly` (ADR 0063) is listed until the API client is regenerated from the spec (mm-qa); then the intersection is a no-op. */
export type InventoryRulesDef = Schemas['InventoryRulesDef'] & { raidKitHeldOnly?: boolean };
/** shared-cs `LoadoutRulesDef` (`loadoutRules`; null/absent or no slots = the built-in four). */
export type LoadoutAcceptsDef = Schemas['LoadoutAcceptsDef'];
export type LoadoutSlotDef = Schemas['LoadoutSlotDef'];
export type LoadoutRulesDef = { slots?: (LoadoutSlotDef | null)[] | null; maxGoods?: number };

/** shared-cs `ItemMobilities`: goods (`loadout`), raw materials (`haul`), or "" = the default from the category. */
export const ITEM_MOBILITIES = ['loadout', 'haul'] as const;

/** shared-cs `DerivedEffectDef` (`statusEffects[].derived`): a home skill from the best stat at home. */
export type DerivedEffectDef = Schemas['DerivedEffectDef'];
/** shared-cs `HomeSkillIds`, with the modifier key each skill writes (a perk's bonus is its modifier on that key, ADR 0066). */
export const HOME_SKILLS = {
  build: { key: 'base.buildTime' },
  beds: { key: 'base.bedRate' },
} as const;

/** The new fields on existing defs. */
export type ItemHbFields = { mobility?: string };
export type ModuleLevelHbFields = {
  beds?: number;
  bedDurationMult?: number;
  bedHealPerDay?: number;
};
export type ConditionHbFields = { statMods?: Record<string, number> | null };
export type BasesHbFields = {
  caravansEnabled?: boolean;
  /** ADR 0061: home moves by itself only while on (C# default true; the seed sets false). */
  autoHomeEnabled?: boolean;
  autoHomeWindowDays?: number;
  autoHomeLeadDays?: number;
  autoHomeMinUseDays?: number;
  autoHomeFlagQuietHours?: number;
  /** Home menu H1 (card #423): the phone's automatic drop waits for a settled, fresh GPS fix. */
  autoDropSettleFixes?: number;
  /** Card #538: the rolling window (samples) the settle gate looks at; >= autoDropSettleFixes. */
  autoDropWindowFixes?: number;
  autoDropSettleSeconds?: number;
  autoDropMaxAccuracyM?: number;
  autoDropMaxFixAgeSeconds?: number;
  autoDropBackoffSeconds?: number;
  autoDropBackoffMaxSeconds?: number;
};
